import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions } from '../../base';
import '../../styles/css/AdminMain.css';
import Swal from 'sweetalert2';
import { ErrorMessage, Toast } from '../utils/HelpfulFunction';
import { loadServerCapabilities } from '../utils/serverCapabilities';
import {
    visibleFeatures,
    isAvailable,
    loginIsActive,
    identityIsVerifiable,
    CAPABILITIES
} from '../utils/featureAvailability';
import {
    isOnForEveryone,
    overrideCount,
    isReachable,
    parentToOpen,
    worksWithStatus,
    worksWithSummary,
    liveChildren,
    namesOf,
    isDrawnInside,
    dependencyClause,
    alreadyOnClause,
    dependentsClause
} from '../utils/featureReachability';
import { SIGN_IN_PAUSE } from '../utils/experienceChooser';

// Feature flags — ship a build with new screens hidden, then switch them on
// here. Two layers: an "Everyone" default per feature, and per-person overrides
// that can turn a feature ON for a tester while it is off for everyone, or OFF
// for someone while it is on for everyone.
//
// The app defaults every feature to OFF when it can't read a flag, so a tenant
// that never opens this screen simply never sees these surfaces.

// Must match FEATURES in the app's src/features/FeatureFlagProvider.js — the
// app only reads these ids, so inventing new ones here does nothing.
//
// Two independent facts per feature, which used to be one.
//
// `containedIn` is another FEATURE this one cannot work without — usually
// because it is DRAWN inside it. Our choice, and changeable: it decides
// reachability, not capability. Turning a section on while its container is off
// switches on something no fan can reach. The fan app applies the same gate
// through its FEATURE_PARENTS.
//
// `relation: 'worksWith'` marks the one kind of container that is not drawn
// around the feature: the Choose Experience screen is a screen of its own, but
// it only works while the Change Experience menu is on. Same gate, different
// sentence — "a section inside" would be untrue of it. See featureReachability.
//
// `needs` is what the CLUB must have for this feature to work at all. Not our
// choice. Declared on the feature that actually needs it and never inherited —
// a link offer needs no account, and says so, even though the only place it is
// currently drawn is inside a screen that does. See utils/featureAvailability.
const FEATURE_CATALOGUE = [
    {
        id: 'profile',
        label: 'My Profile',
        description: 'The profile screen, its icon in the top bar and the MY PROFILE row in the side menu. Turning this off hides the whole area regardless of the settings below.',
        // Every door to it refuses an anonymous session, and it has no email or
        // name to show without one.
        needs: [CAPABILITIES.FAN_ACCOUNTS]
    },
    {
        id: 'offers',
        label: 'Offers',
        description: 'The MY OFFERS section inside the profile — offer cards, link offers and coded offers with their QR codes.',
        containedIn: 'profile',
        // Nothing of its own. A link offer is a card and a URL out; the `offers`
        // node is world-readable and no server call is involved. A coded offer
        // does need an account, but that is a property of the SHAPE, refused
        // where the shape is chosen, not of the whole surface.
        needs: []
    },
    {
        id: 'verification',
        label: 'Season Ticket Verification',
        description: 'The Verify Status button that checks a fan\'s Ticketmaster login against the season ticket roster.',
        containedIn: 'profile',
        // Nothing of its own beyond the account it lives behind.
        //
        // This briefly declared VERIFIED_IDENTITY — "the server can confirm who
        // a fan is" — which reads correctly and was exactly backwards. That
        // capability is TM_API_KEY, so a club without Ticketmaster lost the
        // switch, lost the Verification screen with it, and could therefore
        // never upload the roster or set the identity source that would have
        // made verification work. The screen that CONFIGURES a capability must
        // not be gated on that capability, or the club that most needs it is
        // the one club that cannot reach it.
        needs: []
    },
    {
        id: 'experienceSwitcher',
        label: 'Change Experience',
        description: 'The Change Experience list in the app side menu. Only ever appears when two or more experiences exist.',
        needs: []
    },
    {
        id: 'profilePhoto',
        label: 'Profile Photo',
        description: 'Lets a fan set a profile picture from their photo library. The photo is visible only to the fan who set it.',
        containedIn: 'profile',
        needs: []
    },
    {
        id: 'experienceChooser',
        label: 'Choose Experience screen',
        description: `A full-screen "Choose your Experience" page of big image buttons, one per experience, shown to fans after they sign in — and once on each phone for fans who were already signed in, unless they had already picked an experience from the side menu. It only appears while Change Experience is on and at least two buttons are set up on the Experiences page. While two or more buttons are set up but it is not on for everyone, every sign-in pauses briefly (${SIGN_IN_PAUSE}) while the app checks whether that fan is a tester — so if you switch it off for good, remove its buttons too.`,
        // Said when it is switched off (or goes dark with Change Experience):
        // the fan app keeps checking each sign-in for as long as its buttons
        // are set up, whatever this switch says.
        offNote: `While its buttons are still set up on the Experiences page, every sign-in keeps pausing briefly (${SIGN_IN_PAUSE}) while the app checks whether that fan is a tester. If you are switching it off for good, remove the buttons too.`,
        // Not drawn inside the Change Experience menu, but switched off with
        // it: the fan app ignores a fan's choice while the menu is off, so a
        // chooser then would ask a question whose answer does nothing.
        containedIn: 'experienceSwitcher',
        relation: 'worksWith',
        // It is shown on the way out of sign-in, and a club with the login
        // flow hidden has no sign-in for it to follow.
        needs: [CAPABILITIES.FAN_ACCOUNTS]
    }
];

class SetUpFeatureFlags extends Component {
    constructor(props) {
        super(props);
        this.state = {
            flags: {},
            access: {},
            testers: {},
            // Whether any fan of this club can sign in. Starts true so a slow
            // read never blinks a switch out of existence.
            loginActive: true,
            // What only the server knows. Null until it answers, and null reads
            // as "capable" — a switch missing because a request was slow is
            // worse than one that turns out to do nothing.
            serverCapabilities: null,
            loading: true,
            searchEmail: ''
        };
        this.handleChange = this.handleChange.bind(this);
        this.addTester = this.addTester.bind(this);
    }

    componentDidMount() {
        this.loadData();
        this.loadServerCapabilities();
    }

    // Whether a verification can complete is decided by a key in the functions
    // environment, which this browser cannot read. A failure here leaves
    // serverCapabilities null, which withholds nothing.
    async loadServerCapabilities() {
        this.setState({ serverCapabilities: await loadServerCapabilities(this.props.userData) });
    }

    async loadData() {
        try {
            const [flagsSnap, accessSnap, testersSnap, settingsSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('featureFlags').once('value'),
                appDatabasePrimaryFunctions.ref('featureAccess').once('value'),
                appDatabasePrimaryFunctions.ref('featureTesters').once('value'),
                appDatabasePrimaryFunctions.ref('settings').once('value')
            ]);
            // Only the ROOT settings decide whether fans can sign in — the app
            // latches that from the root before any experience resolves. The
            // per-experience read this used to do could not answer no anyway:
            // the default experience keeps its settings at the root, so it
            // always contributed an empty object that passed.
            this.setState({
                flags: flagsSnap.val() || {},
                access: accessSnap.val() || {},
                testers: testersSnap.val() || {},
                loginActive: loginIsActive(settingsSnap.val()),
                loading: false
            });
        } catch (e) {
            console.log('Error loading feature flags:', e);
            this.setState({ loading: false });
        }
    }

    handleChange(evt) {
        const target = evt.target;
        this.setState({ [target.name]: target.value });
    }

    // What this club has. One source of truth: the rows on screen and the
    // guard below must agree, or a row can be offered and refused, or withheld
    // and still switchable.
    capabilities() {
        return {
            [CAPABILITIES.FAN_ACCOUNTS]: this.state.loginActive,
            [CAPABILITIES.VERIFIED_IDENTITY]: identityIsVerifiable(this.state.serverCapabilities)
        };
    }

    // The "Everyone" default for a feature.
    async setGlobal(featureId, enabled) {
        const feature = FEATURE_CATALOGUE.find(f => f.id === featureId) || {};
        const flagState = { flags: this.state.flags, access: this.state.access };
        // A row for a feature this club cannot use is only on screen so it can
        // be turned OFF — it is kept by hasLiveState, which counts a per-person
        // `false` override as live state. A stale override from a removed tester
        // therefore resurrected the row, and nothing here stopped it being
        // switched back ON, which is a back door into a feature the same screen
        // has just said the club cannot use. Turning it off stays allowed; that
        // is the whole reason the row is there.
        if (enabled && !isAvailable(feature, this.capabilities(), FEATURE_CATALOGUE)) {
            ErrorMessage.fire({
                title: `${feature.label} cannot work here`,
                text: 'This row is only still on screen so it can be switched off — turning it back on would show fans something that cannot load for them.'
            });
            return;
        }
        // A section switched on alone does nothing: its container is the only
        // way in. Rather than refuse, open both — the thing being asked for is
        // "show me offers", and one switch of two is never what that meant.
        //
        // A feature that only WORKS alongside another (the Choose Experience
        // screen) needs its partner on for everyone, not just for somebody:
        // the app gives it per person only to fans who have both, so beside a
        // partner on for testers "on for everyone" reaches those testers and
        // nobody else. Say how few, and offer both — or this one alone, which
        // is a real choice while the partner is still being tried out.
        const parent = enabled ? parentToOpen(feature, FEATURE_CATALOGUE, flagState) : null;
        const alsoOn = [];
        let partial = null;
        if (parent) {
            const partnerTried = !isDrawnInside(feature) && isReachable(parent.id, flagState);
            const reach = partnerTried
                ? worksWithStatus(feature.id, parent.id, {
                    flags: { ...this.state.flags, [feature.id]: { enabled: true } },
                    access: this.state.access
                }).testers
                : 0;
            const people = `${reach} ${reach === 1 ? 'person' : 'people'}`;
            const result = await Swal.fire({
                title: `Turn on ${feature.label} and ${parent.label}?`,
                text: partnerTried
                    ? `${dependencyClause(feature, parent)}, which is on for testers only — on its own, ${feature.label} on for everyone would reach just ${people}. Turning both on shows ${feature.label} to every fan whose build has it.`
                    : `${dependencyClause(feature, parent)}, which is hidden from everyone — on its own it would change nothing for fans. Turning both on shows ${feature.label} to every fan whose build has it.`,
                showCancelButton: true,
                confirmButtonText: 'Turn on both',
                ...(partnerTried ? { showDenyButton: true, denyButtonText: `Only ${feature.label}` } : {})
            });
            if (partnerTried && result && result.isDenied) {
                partial = { parent, people, reach };
            } else {
                if (!result.value) return;
                alsoOn.push(parent);
            }
        } else if (enabled) {
            const testerCount = overrideCount(featureId, this.state.access, false);
            // Switching a container on also lets out whatever is already on
            // inside it — the surprise being closed here is a profile arriving
            // with an offers section nobody remembered switching on.
            const revealed = liveChildren(featureId, FEATURE_CATALOGUE, flagState);
            const revealedText = revealed.length > 0
                ? ` ${namesOf(revealed)} ${alreadyOnClause(revealed)}, so ${revealed.length === 1 ? 'it appears' : 'they appear'} too.`
                : '';
            const result = await Swal.fire({
                title: `Turn on ${feature.label} for everyone?`,
                text: (testerCount > 0
                    ? `Every fan on a build that includes this feature will see it. ${testerCount} person(s) are set to OFF and will stay hidden.`
                    : 'Every fan on a build that includes this feature will see it.') + revealedText,
                showCancelButton: true,
                confirmButtonText: 'Turn on for everyone'
            });
            if (!result.value) return;
        } else {
            // Switching a container off takes its sections with it. They keep
            // their own settings and come back when it does, which is the half
            // that stops this reading as "did I just lose that configuration".
            const affected = liveChildren(featureId, FEATURE_CATALOGUE, flagState);
            // What switching off does NOT stop (the chooser's sign-in pause),
            // from the feature itself or anything going dark with it.
            const notes = [feature, ...affected].map(f => f.offNote).filter(Boolean);
            if (affected.length > 0) {
                const result = await Swal.fire({
                    title: `Turn off ${feature.label}?`,
                    text: `${namesOf(affected)} ${dependentsClause(affected)}, so ${affected.length === 1 ? 'it goes' : 'they go'} dark for fans too. ${affected.length === 1 ? 'Its' : 'Their'} own switches stay as they are and take effect again when you turn ${feature.label} back on.`
                        + notes.map(note => ` ${note}`).join(''),
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonText: `Turn off ${feature.label}`
                });
                if (!result.value) return;
            } else if (notes.length > 0) {
                const result = await Swal.fire({
                    title: `Turn off ${feature.label}?`,
                    text: `Fans stop seeing it. ${notes.join(' ')}`,
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonText: `Turn off ${feature.label}`
                });
                if (!result.value) return;
            }
        }
        this.setState({ loading: true });
        try {
            // One write, so a fan can never load the app between the two and
            // find a section on with its container still shut.
            const updates = {};
            [{ feature, enabled: !!enabled }, ...alsoOn.map(f => ({ feature: f, enabled: true }))]
                .forEach(({ feature: f, enabled: value }) => {
                    updates[`featureFlags/${f.id}/label`] = f.label || f.id;
                    updates[`featureFlags/${f.id}/enabled`] = value;
                    updates[`featureFlags/${f.id}/updatedAt`] = Date.now();
                });
            await appDatabasePrimaryFunctions.ref().update(updates);
            Toast.fire({
                title: enabled
                    ? (alsoOn.length > 0
                        ? `${feature.label} and ${namesOf(alsoOn)} are on for everyone`
                        : (partial
                            ? `${feature.label} is switched on for everyone — ${partial.people} ${partial.reach === 1 ? 'gets' : 'get'} it until ${partial.parent.label} is too`
                            : `${feature.label} is on for everyone`))
                    : `${feature.label} is hidden from everyone except testers`
            });
            await this.loadData();
        } catch (e) {
            console.log('Error saving feature flag:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Could not save that change, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    // '' = follow the Everyone setting, true = always on, false = always off.
    async setOverride(uid, featureId, value) {
        this.setState({ loading: true });
        try {
            const ref = appDatabasePrimaryFunctions.ref(`featureAccess/${uid}/${featureId}`);
            if (value === '') {
                await ref.remove();
            } else {
                await ref.set(value === 'true');
            }
            await this.loadData();
        } catch (e) {
            console.log('Error saving feature access:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Could not save that change, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    async addTester() {
        const email = (this.state.searchEmail || '').trim();
        if (!email) {
            ErrorMessage.fire({ title: 'No email entered', text: 'Enter the email address of the account you want to give early access to.' });
            return;
        }
        this.setState({ loading: true });
        try {
            // usernameToEmail is indexed on email and carries the uid — the same
            // lookup User Management uses.
            let snapshot = await appDatabasePrimaryFunctions.ref('usernameToEmail')
                .orderByChild('email').equalTo(email.toLowerCase()).once('value');
            if (!snapshot.exists() && email !== email.toLowerCase()) {
                snapshot = await appDatabasePrimaryFunctions.ref('usernameToEmail')
                    .orderByChild('email').equalTo(email).once('value');
            }
            const matches = snapshot.val() || {};
            const record = Object.values(matches)[0];
            if (!record || !record.uid) {
                this.setState({ loading: false });
                ErrorMessage.fire({
                    title: 'No account found',
                    text: `No app account is registered to ${email}. The person has to create their account in the app first — early access is granted to an account, not to a device.`
                });
                return;
            }
            if (this.state.testers[record.uid]) {
                this.setState({ loading: false, searchEmail: '' });
                Toast.fire({ title: 'That person is already on the list' });
                return;
            }
            await appDatabasePrimaryFunctions.ref(`featureTesters/${record.uid}`).set({
                email: record.email || email,
                userName: record.userName || '',
                addedAt: Date.now()
            });
            Toast.fire({ title: `${record.userName || record.email} added — switch on the features they should see` });
            this.setState({ searchEmail: '' });
            await this.loadData();
        } catch (e) {
            console.log('Error adding tester:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Could not look that account up, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    async removeTester(uid, name) {
        const result = await Swal.fire({
            title: `Remove ${name}?`,
            text: 'They go back to whatever every other fan sees. Nothing else about their account changes.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove'
        });
        if (!result.value) return;
        this.setState({ loading: true });
        try {
            await appDatabasePrimaryFunctions.ref().update({
                [`featureTesters/${uid}`]: null,
                [`featureAccess/${uid}`]: null
            });
            Toast.fire({ title: 'Removed' });
            await this.loadData();
        } catch (e) {
            console.log('Error removing tester:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Could not remove that person, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    isGlobalOn(featureId) {
        return isOnForEveryone(featureId, this.state.flags);
    }

    // How many people are explicitly set to on (or off) for a feature.
    overrideCount(featureId, wanted) {
        return overrideCount(featureId, this.state.access, wanted);
    }

    summaryFor(featureId) {
        const on = this.overrideCount(featureId, true);
        const off = this.overrideCount(featureId, false);
        const base = this.isGlobalOn(featureId)
            ? (off > 0 ? `On for everyone, except ${off} person(s)` : 'On for everyone')
            : (on > 0 ? `Hidden — on for ${on} tester(s)` : 'Hidden from everyone');
        const feature = FEATURE_CATALOGUE.find(f => f.id === featureId);
        // A feature that only works alongside another (the chooser) is not
        // inside anything, so it is never "unreachable" — and it is counted per
        // person, the way the app counts it: the same count the chooser
        // editor's status line shows (worksWithStatus), so the two never
        // disagree, and never "On for everyone" unless everyone gets it.
        if (feature && feature.containedIn && !isDrawnInside(feature)) {
            const parent = FEATURE_CATALOGUE.find(f => f.id === feature.containedIn);
            return worksWithSummary(feature, parent, {
                flags: this.state.flags,
                access: this.state.access,
                available: isAvailable(feature, this.capabilities(), FEATURE_CATALOGUE)
            });
        }
        // Offers and verification live INSIDE the profile screen, so saying they
        // are "on for everyone" while the profile is hidden would be a lie.
        if (feature && feature.containedIn && !this.isEffectivelyReachable(feature.containedIn)) {
            const parent = FEATURE_CATALOGUE.find(f => f.id === feature.containedIn);
            return `${base} — but unreachable while ${parent ? parent.label : feature.containedIn} is off`;
        }
        return base;
    }

    // A parent feature is reachable if it's on for everyone or on for at least
    // one person; anything else means nobody can get to what sits inside it.
    isEffectivelyReachable(featureId) {
        return isReachable(featureId, { flags: this.state.flags, access: this.state.access });
    }

    // featureAccess entries with no matching featureTesters record still apply —
    // they'd otherwise be invisible here while quietly changing what someone sees.
    orphanOverrides() {
        return Object.entries(this.state.access || {})
            .filter(([uid, overrides]) =>
                !this.state.testers[uid] &&
                overrides && Object.values(overrides).some(v => v === true || v === false))
            .map(([uid, overrides]) => ({ uid, overrides }));
    }

    render() {
        const { testers, access, flags, loginActive } = this.state;
        // Only what this club can use, plus anything already switched on that
        // it cannot — that still needs a way to be switched back off.
        const features = visibleFeatures(FEATURE_CATALOGUE, this.capabilities(), { flags, access });
        const testerList = Object.entries(testers)
            .map(([uid, tester]) => ({ uid, ...tester }))
            .sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));
        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }}/>
                <SideMenu/>
                <TopMenu/>
                <div className="admin-main-panel">
                    <p className="admin-page-eyebrow">Access</p>
                    <p className="admin-header-text">Feature Access</p>
                    <p style={{ fontSize: 13, color: 'grey', fontWeight: 'normal', maxWidth: 800, marginLeft: 20 }}>
                        Control which parts of the app are visible, without shipping a new version to the app stores.
                        Features start hidden from everyone: switch one on for a few testers first, then for everyone when
                        you're happy with it. Fans need a build that contains the feature — anyone on an older version
                        simply won't see it either way.
                    </p>

                    <div className="admin-form-box" style={{ maxWidth: 760 }}>
                        <p style={{ fontWeight: 'bold', fontSize: 16 }}>Features</p>
                        {!loginActive &&
                            <p style={{ fontSize: 13, color: '#8a5a06', fontWeight: 'normal' }}>
                                Fans of this app can't sign in — <b>Hide Login Flow</b> is on in Settings — so anything
                                that needs an account isn't listed here.
                            </p>
                        }
                        <div className="admin-table-wrap">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>Right now</th>
                                        <th style={{ width: 140 }}>Everyone</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {features.map(feature => (
                                        <tr key={feature.id}>
                                            <td>
                                                <span style={{ fontWeight: 'bold' }}>{feature.label}</span>
                                                <div style={{ fontSize: 12, color: 'grey' }}>{feature.description}</div>
                                                {feature.unavailable &&
                                                    <div style={{ fontSize: 12, color: '#8a5a06' }}>
                                                        Needs a fan account, and this app's login flow is hidden — nobody
                                                        can reach it. Switch it off, or turn the login flow back on in
                                                        Settings.
                                                    </div>
                                                }
                                            </td>
                                            <td style={{ fontSize: 13 }}>{this.summaryFor(feature.id)}</td>
                                            <td>
                                                <label className="switch" style={{ width: 60 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={this.isGlobalOn(feature.id)}
                                                        onChange={(e) => this.setGlobal(feature.id, e.target.checked)}
                                                    />
                                                    <span className="slider round"/>
                                                </label>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="admin-form-box" style={{ maxWidth: 900 }}>
                        <p style={{ fontWeight: 'bold', fontSize: 16 }}>Early access</p>
                        <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                            Add the people who should see a feature before everyone else. <b>Follow</b> means they get
                            whatever the Everyone switch says; <b>On</b> shows it to them while it's hidden;
                            <b> Off</b> hides it from them while it's on for everyone. They must be signed in to their
                            account in the app — early access follows the account, not the phone.
                        </p>
                        <div className="form-group" style={{ display: 'flex', gap: 10, alignItems: 'center', maxWidth: 520 }}>
                            <input
                                type="email"
                                name="searchEmail"
                                className="form-control"
                                value={this.state.searchEmail}
                                onChange={this.handleChange}
                                placeholder="person@example.com"
                            />
                            <button className="btn btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={this.addTester}>Add person</button>
                        </div>

                        {this.orphanOverrides().length > 0 &&
                            <p style={{ fontSize: 13, color: '#8a5a06' }}>
                                {this.orphanOverrides().length} account(s) have feature overrides but aren't on this list —
                                they were removed from it, or set directly in the database. Their overrides are still in effect.
                                Account id(s): {this.orphanOverrides().map(o => o.uid).join(', ')}
                            </p>
                        }
                        {testerList.length === 0 ? (
                            <p style={{ fontSize: 13, color: 'grey' }}>Nobody has early access yet.</p>
                        ) : (
                            <div className="admin-table-wrap">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Person</th>
                                            {features.map(feature => (
                                                <th key={feature.id}>{feature.label}</th>
                                            ))}
                                            <th/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {testerList.map(tester => {
                                            const overrides = (access && access[tester.uid]) || {};
                                            return (
                                                <tr key={tester.uid}>
                                                    <td>
                                                        <span style={{ fontWeight: 'bold' }}>{tester.userName || tester.email}</span>
                                                        {tester.userName && <div style={{ fontSize: 12, color: 'grey' }}>{tester.email}</div>}
                                                    </td>
                                                    {features.map(feature => {
                                                        const value = overrides[feature.id] === true ? 'true'
                                                            : overrides[feature.id] === false ? 'false' : '';
                                                        return (
                                                            <td key={feature.id}>
                                                                <select
                                                                    className="form-control"
                                                                    style={{ minWidth: 100 }}
                                                                    value={value}
                                                                    onChange={(e) => this.setOverride(tester.uid, feature.id, e.target.value)}
                                                                >
                                                                    <option value="">Follow ({this.isGlobalOn(feature.id) ? 'on' : 'off'})</option>
                                                                    <option value="true">On</option>
                                                                    <option value="false">Off</option>
                                                                </select>
                                                            </td>
                                                        );
                                                    })}
                                                    <td>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => this.removeTester(tester.uid, tester.userName || tester.email)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SetUpFeatureFlags;
